import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { settledList } from '../../utils/loadState'
import { getCustomer, getRegisters, uploadRegister } from '../../api'
import './Registers.css'

export default function Registers() {
  const { customerId } = useParams()
  const [customer, setCustomer] = useState(null)
  const [registers, setRegisters] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [vendorFormat, setVendorFormat] = useState('ukg_pro')
  // A failed load is an error with a Retry, never "No registers uploaded" (item 201).
  const [registersError, setRegistersError] = useState(null)

  async function load() {
    setLoading(true)
    try {
      const [ctx, reg] = await Promise.allSettled([
        getCustomer(customerId), getRegisters(customerId),
      ])
      if (ctx.status === 'fulfilled') setCustomer(ctx.value)
      const rl = settledList(reg, 'registers')
      setRegisters(rl.items || [])
      setRegistersError(rl.error)
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [customerId])

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      await uploadRegister(file, customerId, vendorFormat)
      const reg = await getRegisters(customerId)
      setRegisters(reg?.registers || [])
    } catch (err) { alert('Upload failed: ' + err.message) }
    finally { setUploading(false) }
  }, [customerId, vendorFormat])

  const companyName = customer?.company_name || customerId
  const breadcrumbs = [
    { label: 'Customers', href: '/customers' },
    { label: companyName, href: `/customers/${customerId}` },
    { label: 'Registers' },
  ]

  if (loading) return <><TopNav breadcrumbs={breadcrumbs} /><div className="rg-loading"><div className="rg-spinner" /><p>Loading…</p></div></>

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} />
      <div className="rg-page">
        <div className="rg-header">
          <h1 className="rg-title">Payroll Registers</h1>
          <p className="rg-subtitle">{registersError ? 'Registers could not be loaded' : `${registers.length} register${registers.length !== 1 ? 's' : ''}`} for {companyName}</p>
        </div>

        {registersError ? (
          <LoadError what="registers" error={registersError} onRetry={load} />
        ) : registers.length > 0 ? (
          <div className="rg-list">
            {registers.map((r, i) => (
              <div key={i} className="rg-card">
                <div className="rg-card-icon">REG</div>
                <div className="rg-card-info">
                  <div className="rg-card-name">{r.name || r.file_name || `Register ${i + 1}`}</div>
                  <div className="rg-card-meta">
                    {r.employee_count && `${r.employee_count} employees`}
                    {r.variance_count !== undefined && ` · ${r.variance_count} variances`}
                    {r.vendor_format && ` · ${r.vendor_format}`}
                    {r.uploaded_at && ` · ${new Date(r.uploaded_at).toLocaleDateString()}`}
                  </div>
                </div>
                <span className={`rg-card-status ${r.variance_count > 0 ? 'warn' : 'ok'}`}>
                  {r.variance_count > 0 ? `${r.variance_count} variances` : 'Clean'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="rg-empty">
            <div className="rg-empty-icon">📋</div>
            <h3>No registers uploaded</h3>
            <p>Upload a payroll register to compare against expected calculations.</p>
          </div>
        )}

        <div className="rg-upload-section">
          <h3 className="rg-section-title">Upload Register</h3>
          <div className="rg-upload-row">
            <select className="rg-select" value={vendorFormat} onChange={e => setVendorFormat(e.target.value)}>
              <option value="ukg_pro">UKG Pro</option>
              <option value="adp_wfn">ADP Workforce Now</option>
              <option value="workday">Workday</option>
              <option value="generic">Generic CSV</option>
            </select>
            <label className="rg-upload-btn">
              {uploading ? 'Uploading…' : '📁 Choose File'}
              <input type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
            </label>
          </div>
        </div>
      </div>
    </>
  )
}
